import { NavLink } from 'react-router-dom';
import { ChevronsLeft, ChevronsRight, X } from 'lucide-react';
import { useT } from '../../i18n';
import { usePermission } from '../../auth/permissions';
import { IconButton } from '../../ui';
import { cx } from '../../ui/cx';
import { Brand } from '../Brand';
import { NAV_ITEMS, type NavItem } from '../nav';
import { OrgSwitcher } from './OrgSwitcher';
import s from './Sidebar.module.css';

function SidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const { t } = useT();
  const allowed = usePermission(item.permission ?? [], item.mode);
  if (item.permission && !allowed) return null;
  const Icon = item.icon;
  return (
    <li>
      <NavLink
        to={item.path}
        end={item.path === '/'}
        className={({ isActive }) => cx(s.link, isActive && s.active)}
        title={collapsed ? t(item.labelKey) : undefined}
      >
        <Icon size={20} aria-hidden="true" />
        <span className={s.label}>{t(item.labelKey)}</span>
      </NavLink>
    </li>
  );
}

export interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onToggleCollapsed: () => void;
  onCloseMobile: () => void;
}

export function Sidebar({ collapsed, mobileOpen, onToggleCollapsed, onCloseMobile }: SidebarProps) {
  const { t } = useT();
  return (
    <aside className={cx(s.sidebar, collapsed && s.collapsed, mobileOpen && s.open)} aria-label={t('shell.navigation')}>
      <div className={s.head}>
        <Brand compact={collapsed} />
        <span className={s.closeMobile}>
          <IconButton label={t('ui.close')} icon={<X size={18} />} size="sm" onClick={onCloseMobile} />
        </span>
      </div>
      <OrgSwitcher collapsed={collapsed} />
      <nav className={s.nav}>
        <ul className={s.list}>
          {NAV_ITEMS.map((item) => (
            <SidebarLink key={item.id} item={item} collapsed={collapsed} />
          ))}
        </ul>
      </nav>
      <button type="button" className={s.collapse} onClick={onToggleCollapsed} aria-pressed={collapsed} title={collapsed ? t('shell.expand') : t('shell.collapse')}>
        {collapsed ? <ChevronsRight size={18} className="flip-rtl" /> : <ChevronsLeft size={18} className="flip-rtl" />}
        <span className={s.label}>{t('shell.collapse')}</span>
      </button>
    </aside>
  );
}
